import React from "react";
import { FileSearch } from "lucide-react";
import { fmtUSD, fmtPct, timeAgo, truncWallet } from "@/lib/format";
import InfoTip from "@/components/InfoTip";

/**
 * Evidence panel (M13) — shows the raw records supporting a signal.
 * Always deterministic; never AI-generated.
 *
 * v2.5F: every field now has a hover-info tooltip; Best Bid/Ask/Spread
 * fall back to the market's per-outcome CLOB reading when the signal
 * snapshot is empty.
 */
export default function EvidencePanel({ signal, market, consensus, traders = [] }) {
  // Fall back to market.outcomes[i] when signal-level fields are null
  const outcomes = market?.outcomes || [];
  const activeOutcome = outcomes.find(
    (o) => o.name === signal.outcome || o.token_id === signal.token_id
  ) || outcomes[0] || {};
  const bestBid = signal.best_bid ?? activeOutcome.best_bid;
  const bestAsk = signal.best_ask ?? activeOutcome.best_ask;
  const spread  = signal.spread   ?? activeOutcome.spread;

  const items = [
    {
      label: "Participating traders",
      value: traders.length,
      tip: "Number of tracked wallets aligned on this signal's direction.",
    },
    {
      label: "Estimated avg entry",
      value: signal.estimated_average_entry != null
              ? fmtPct(signal.estimated_average_entry, 1) : "—",
      tip: "Volume-weighted average price at which the participating traders opened their positions on this outcome.",
    },
    {
      label: "Current probability",
      value: signal.current_probability != null
              ? fmtPct(signal.current_probability, 1) : "—",
      tip: "Where the market is trading right now for this outcome. Compare to Estimated Avg Entry to gauge how much has priced in.",
    },
    {
      label: "Consensus score",
      value: consensus?.consensus_score != null
              ? Math.round(consensus.consensus_score) : "—",
      tip: "0–100 score of how unanimous tracked traders are on this market. Higher = stronger agreement, weighted by trader quality and position size.",
    },
    {
      label: "Concentration",
      value: consensus?.concentration != null
              ? `${Math.round(consensus.concentration * 100)}%` : "—",
      tip: "How lopsided the consensus is. 100% = every tracked trader agrees. 50% = evenly split. High concentration + few traders can mean single-wallet dominance.",
    },
    {
      label: "Market liquidity",
      value: fmtUSD(signal.liquidity ?? market?.liquidity),
      tip: "Depth of the order book — how much you could theoretically trade before moving the price meaningfully.",
    },
    {
      label: "Best bid / ask",
      value: (bestBid != null || bestAsk != null)
              ? `${bestBid != null ? fmtPct(bestBid, 1) : "—"} / ${bestAsk != null ? fmtPct(bestAsk, 1) : "—"}`
              : "—",
      tip: "Highest current buy order (bid) and lowest current sell order (ask) on the CLOB for this outcome. Falls back to the market's most recent per-outcome reading when signal snapshot is empty.",
    },
    {
      label: "Spread",
      value: spread != null ? `${(spread * 100).toFixed(2)}pp` : "—",
      tip: "Difference between best ask and best bid, in probability points. Tighter spread = better fill; wider spread = higher slippage cost.",
    },
    {
      label: "Detected",
      value: timeAgo(signal.detected_at),
      tip: "When we first observed the pattern that generated this signal.",
    },
  ];
  return (
    <section className="border border-zinc-800 rounded-md p-4" data-testid="evidence-panel">
      <div className="flex items-center gap-2 mb-3">
        <FileSearch size={14} className="text-zinc-400" />
        <h3 className="font-heading font-semibold text-zinc-100">Evidence</h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          deterministic · from stored records
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        {items.map((it) => (
          <div key={it.label} className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <span>{it.label}</span>
              <InfoTip content={it.tip} testid={`evidence-tip-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
            </div>
            <div className="text-zinc-100 font-mono-num truncate">{it.value ?? "—"}</div>
          </div>
        ))}
      </div>
      {traders.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Position-sizing per trader
          </div>
          <div className="border border-zinc-800 rounded-sm">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/40">
                <tr>
                  <th className="text-left px-2 py-1">Trader</th>
                  <th className="text-right px-2 py-1">Score</th>
                </tr>
              </thead>
              <tbody>
                {traders.slice(0, 10).map((t) => (
                  <tr key={t.id} className="border-t border-zinc-800/70">
                    <td className="px-2 py-1 text-zinc-200">
                      {t.public_username || truncWallet(t.wallet_address)}
                    </td>
                    <td className="px-2 py-1 text-right font-mono-num text-zinc-300">
                      {t.score != null ? Math.round(t.score) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
